import React, { useState, useRef, useEffect, useMemo, useCallback, useReducer } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
    X, Send, Sparkles,
    Image,
    Clock,
    ChevronLeft,
    Loader2, AlertCircle, RefreshCw
} from 'lucide-react';
import {
    getInstagramContentType,
    InstagramReelsPreview,
    InstagramComingSoonPreview,
    InstagramPostPreview
} from './InstagramChannel';
import {
    YouTubeShortsPreview,
    YouTubeThumbnailPreview,
    ThumbnailCustomizationPanel
} from './YouTube';
import { ShowNotesPreview } from './ShowNotes';
import { BlogPostPreview } from './BlogPost';
import { EmailNewsletterPreview } from './EmailNewsletterPreview';
import LinkedInPreview from './LinkedIn';
import TwitterPreview from './Twitter';
import FacebookPreview from './Facebook';
import { assembleContext } from '../../../ai/services/repurposeAiService';
import comingSoonService from '../../../ai/services/comingSoonService';
import vaultService from '../../../services/vaultService';
import VaultDrawer from '../VaultDrawer';
import { Database } from 'lucide-react';
import { useContentEditor } from '../../../ai/hooks';

// Import all prompt constants from unified contentPrompts module
import {
    // LinkedIn
    LINKEDIN_TEMPLATES,
    LINKEDIN_TONE_OPTIONS as TONE_OPTIONS,
    LINKEDIN_CTA_OPTIONS as CTA_OPTIONS,
    LINKEDIN_QUICK_ACTIONS as QUICK_ACTIONS,

    // Twitter
    TWITTER_TEMPLATES,
    TWITTER_TONE_OPTIONS,
    TWITTER_CTA_OPTIONS,
    TWITTER_QUICK_ACTIONS,

    // Facebook
    FACEBOOK_TEMPLATES,
    FACEBOOK_TONE_OPTIONS,
    FACEBOOK_CTA_OPTIONS,
    FACEBOOK_QUICK_ACTIONS,

    // Show Notes
    SHOW_NOTES_TEMPLATES,
    SHOW_NOTES_TONE_OPTIONS,
    SHOW_NOTES_CTA_OPTIONS,
    SHOW_NOTES_QUICK_ACTIONS,

    // Blog Post
    BLOG_TEMPLATES,
    BLOG_TONE_OPTIONS,
    BLOG_CTA_OPTIONS,
    BLOG_QUICK_ACTIONS,

    // Email Newsletter
    EMAIL_NEWSLETTER_TEMPLATES,
    EMAIL_TONE_OPTIONS,
    EMAIL_CTA_OPTIONS,
    EMAIL_QUICK_ACTIONS
} from '../../../ai/prompts/contentPrompts';

// Platform-specific quick actions mapping
const PLATFORM_QUICK_ACTIONS = {
    linkedin: QUICK_ACTIONS,
    twitter: TWITTER_QUICK_ACTIONS,
    facebook: FACEBOOK_QUICK_ACTIONS,
    instagram: QUICK_ACTIONS, // Use LinkedIn actions as fallback for Instagram
    tiktok: QUICK_ACTIONS,
    youtube: QUICK_ACTIONS,
    'show-notes': SHOW_NOTES_QUICK_ACTIONS,
    'blog-post': BLOG_QUICK_ACTIONS,
    'email-newsletter': EMAIL_QUICK_ACTIONS
};

// Platform-specific templates mapping
const PLATFORM_TEMPLATES = {
    linkedin: LINKEDIN_TEMPLATES,
    twitter: TWITTER_TEMPLATES,
    facebook: FACEBOOK_TEMPLATES,
    instagram: LINKEDIN_TEMPLATES,
    tiktok: LINKEDIN_TEMPLATES,
    youtube: LINKEDIN_TEMPLATES,
    'show-notes': SHOW_NOTES_TEMPLATES,
    'blog-post': BLOG_TEMPLATES,
    'email-newsletter': EMAIL_NEWSLETTER_TEMPLATES
};

// Platform-specific tone options mapping
const PLATFORM_TONE_OPTIONS = {
    linkedin: TONE_OPTIONS,
    twitter: TWITTER_TONE_OPTIONS,
    facebook: FACEBOOK_TONE_OPTIONS,
    instagram: TONE_OPTIONS,
    tiktok: TONE_OPTIONS,
    youtube: TONE_OPTIONS,
    'show-notes': SHOW_NOTES_TONE_OPTIONS,
    'blog-post': BLOG_TONE_OPTIONS,
    'email-newsletter': EMAIL_TONE_OPTIONS
};

// Platform-specific CTA options mapping
const PLATFORM_CTA_OPTIONS = {
    linkedin: CTA_OPTIONS,
    twitter: TWITTER_CTA_OPTIONS,
    facebook: FACEBOOK_CTA_OPTIONS,
    instagram: CTA_OPTIONS,
    tiktok: CTA_OPTIONS,
    youtube: CTA_OPTIONS,
    'show-notes': SHOW_NOTES_CTA_OPTIONS,
    'blog-post': BLOG_CTA_OPTIONS,
    'email-newsletter': EMAIL_CTA_OPTIONS
};

// Animation variants
const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
};

const modalVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            damping: 25,
            stiffness: 300,
            delay: 0.1
        }
    },
    exit: { scale: 0.95, opacity: 0, y: 10, transition: { duration: 0.2 } }
};

const contentVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: 20, transition: { duration: 0.2 } }
};

// Platform configuration
const PLATFORMS = {
    instagram: {
        name: 'Instagram',
        icon: 'instagram',
        color: 'from-pink-500 via-purple-500 to-orange-400',
        bgColor: 'bg-gradient-to-br from-pink-50 to-purple-50',
        textColor: 'text-pink-600',
        borderColor: 'border-pink-200',
    },
    tiktok: {
        name: 'TikTok',
        icon: 'tiktok',
        color: 'from-black via-gray-900 to-gray-800',
        bgColor: 'bg-gradient-to-br from-gray-50 to-black',
        textColor: 'text-gray-900',
        borderColor: 'border-gray-300',
    },
    linkedin: {
        name: 'LinkedIn',
        icon: 'linkedin',
        color: 'from-blue-600 to-blue-700',
        bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
    },
    twitter: {
        name: 'X',
        icon: 'twitter',
        color: 'from-gray-900 to-black',
        bgColor: 'bg-gradient-to-br from-gray-50 to-white',
        textColor: 'text-gray-900',
        borderColor: 'border-gray-200',
    },
    facebook: {
        name: 'Facebook',
        icon: 'facebook',
        color: 'from-blue-600 to-blue-800',
        bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
    },
    youtube: {
        name: 'YouTube Shorts',
        icon: 'youtube',
        color: 'from-red-600 to-red-700',
        bgColor: 'bg-gradient-to-br from-red-50 to-orange-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200',
    },
    'show-notes': {
        name: 'Show Notes',
        icon: 'file-text',
        color: 'from-indigo-600 to-purple-600',
        bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50',
        textColor: 'text-indigo-700',
        borderColor: 'border-indigo-200',
    },
    'blog-post': {
        name: 'Blog Post',
        icon: 'globe',
        color: 'from-emerald-600 to-teal-600',
        bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50',
        textColor: 'text-emerald-700',
        borderColor: 'border-emerald-200',
    },
    'email-newsletter': {
        name: 'Email Newsletter',
        icon: 'mail',
        color: 'from-violet-600 to-purple-600',
        bgColor: 'bg-gradient-to-br from-violet-50 to-purple-50',
        textColor: 'text-violet-700',
        borderColor: 'border-violet-200',
    }
};

// Platform Preview Component
const PlatformPreview = ({
    platform,
    instagramContentType,
    contentType,
    generatedContent = null,
    isStreaming = false,
    guestName = null,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    media = null,
    onMediaChange = null,
    isUploading = false,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false,
    episode = null,
    onOpenVault = null,
    onComingSoonTemplateSelect = null,
    selectedComingSoonTemplate = null
}) => {
    // For Instagram, determine which preview to show based on content type
    const getInstagramPreview = () => {
        if (instagramContentType === 'reels') {
            return InstagramReelsPreview;
        }
        if (instagramContentType === 'coming-soon') {
            return InstagramComingSoonPreview;
        }
        return InstagramPostPreview;
    };

    const previewMap = {
        instagram: getInstagramPreview(),
        linkedin: LinkedInPreview,
        twitter: TwitterPreview,
        facebook: FacebookPreview,
        youtube: YouTubeShortsPreview,
    };

    // Normalize platform key
    const normalizedPlatform = platform?.toLowerCase().includes('youtube') ? 'youtube' : platform;

    // IMPORTANT: Check platform (activePlatform) FIRST before contentType
    // This ensures correct preview when user switches platforms

    // Explicit check for Show Notes - with full props support
    if (platform === 'show-notes') {
        return (
            <ShowNotesPreview
                content={generatedContent}
                isStreaming={isStreaming}
                guestName={guestName}
                isEditable={true}
                onContentChange={onContentChange}
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={onSave}
                episode={episode}
                onSaveToVault={onSaveToVault}
                onStartOver={onStartOver}
                isSavingToVault={isSavingToVault}
            />
        );
    }

    // Explicit check for Blog Post - with full props support
    if (platform === 'blog-post') {
        return (
            <BlogPostPreview
                content={generatedContent}
                isStreaming={isStreaming}
                isEditable={true}
                onContentChange={onContentChange}
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={onSave}
                onSaveToVault={onSaveToVault}
                onStartOver={onStartOver}
                isSavingToVault={isSavingToVault}
            />
        );
    }

    // Explicit check for Email Newsletter - with full props support
    if (platform === 'email-newsletter') {
        return (
            <EmailNewsletterPreview
                content={generatedContent}
                isStreaming={isStreaming}
                isEditable={true}
                onContentChange={onContentChange}
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={onSave}
                onSaveToVault={onSaveToVault}
                onStartOver={onStartOver}
                isSavingToVault={isSavingToVault}
            />
        );
    }

    // Explicit check for Shorts to ensure correct preview
    if (platform === 'youtube' || (contentType?.platform === 'YouTube' && contentType?.id === 'shorts')) {
        return <YouTubeShortsPreview />;
    }

    // Explicit check for Instagram Coming Soon - with props support
    if (instagramContentType === 'coming-soon') {
        return (
            <InstagramComingSoonPreview
                posterUrl={generatedContent?.posterUrl}
                guestName={guestName || generatedContent?.guestName}
                topic={generatedContent?.topic}
                generatedCaption={generatedContent?.caption}
                selectedTemplate={selectedComingSoonTemplate}
                onTemplateSelect={onComingSoonTemplateSelect}
            />
        );
    }

    const PreviewComponent = previewMap[normalizedPlatform] || previewMap[platform] || InstagramPostPreview;

    // Check if the preview component supports content props (LinkedIn, Twitter, and Facebook)
    const supportsContent = normalizedPlatform === 'linkedin' || normalizedPlatform === 'twitter' || normalizedPlatform === 'facebook';

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={`${platform}-${instagramContentType || 'default'}`}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="h-full"
            >
                {supportsContent ? (
                    <PreviewComponent
                        content={generatedContent}
                        isStreaming={isStreaming}
                        guestName={guestName}
                        isEditable={true}
                        onContentChange={onContentChange}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onSave={onSave}
                        media={media}
                        onMediaChange={onMediaChange}
                        isUploading={isUploading}
                        episode={episode}
                        onSaveToVault={onSaveToVault}
                        onStartOver={onStartOver}
                        isSavingToVault={isSavingToVault}
                    />
                ) : (
                    <PreviewComponent />
                )}
            </motion.div>
        </AnimatePresence>
    );
};

// AI Message Bubble Component with Markdown Support
const MessageBubble = ({ message, isUser, isTyping, activeTool }) => {
    // Map raw backend tool names to friendly UI statuses
    const getToolStatus = (tool) => {
        if (!tool) return "Thinking...";
        if (tool === 'orchestrate') return "Analyzing request...";
        if (tool.includes('generate') && tool.includes('post')) return "Drafting your post...";
        if (tool.includes('modify')) return "Refining content...";
        if (tool.includes('generate_caption')) return "Writing caption...";
        if (tool.includes('transcript')) return "Analyzing transcript...";
        if (tool.includes('summary')) return "Summarizing context...";
        return "Processing...";
    };

    // Custom components for markdown rendering
    const markdownComponents = {
        // Bold text
        strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
        ),
        // Paragraphs
        p: ({ children }) => (
            <p className="mb-2 last:mb-0">{children}</p>
        ),
        // Numbered lists
        ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>
        ),
        // Bullet lists
        ul: ({ children }) => (
            <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
        ),
        // List items
        li: ({ children }) => (
            <li className="text-sm">{children}</li>
        ),
        // Headings
        h1: ({ children }) => (
            <h1 className="text-lg font-bold mb-2">{children}</h1>
        ),
        h2: ({ children }) => (
            <h2 className="text-base font-bold mb-2">{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 className="text-sm font-bold mb-1">{children}</h3>
        ),
        // Line breaks
        br: () => <br />,
        // Code blocks
        code: ({ inline, children }) => (
            inline
                ? <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                : <code className="block bg-gray-100 p-2 rounded text-xs font-mono my-1">{children}</code>
        ),
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
        >
            <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
                {!isUser && (
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                            <Sparkles size={12} className="text-white" />
                        </div>
                        <span className="text-[10px] font-semibold text-indigo-600">Mariko AI</span>
                    </div>
                )}
                <div className={`px-4 py-2.5 rounded-2xl ${isUser
                    ? 'bg-indigo-600 text-white rounded-br-md'
                    : 'bg-white border border-gray-100 text-gray-800 rounded-bl-md shadow-sm'
                    }`}>
                    {isTyping ? (
                        <div className="flex items-center gap-2">
                            {activeTool ? (
                                <>
                                    <Loader2 size={14} className="text-indigo-500 animate-spin" />
                                    <span className="text-xs text-indigo-600 font-medium animate-pulse">
                                        {getToolStatus(activeTool)}
                                    </span>
                                </>
                            ) : (
                                <div className="flex items-center gap-1">
                                    <motion.div
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                                        className="w-2 h-2 rounded-full bg-gray-400"
                                    />
                                    <motion.div
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                                        className="w-2 h-2 rounded-full bg-gray-400"
                                    />
                                    <motion.div
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                                        className="w-2 h-2 rounded-full bg-gray-400"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={`text-sm leading-relaxed prose prose-sm max-w-none ${isUser ? 'prose-invert' : ''}`}>
                            <ReactMarkdown components={markdownComponents}>
                                {message}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>
                {isUser && (
                    <p className="text-[10px] text-gray-400 text-right mt-1">Just now</p>
                )}
            </div>
        </motion.div>
    );
};

// AI Assistant State Reducer
const aiPanelReducer = (state, action) => {
    switch (action.type) {
        case 'ADD_MESSAGE':
            return { ...state, messages: [...state.messages, action.payload] };
        case 'SET_MESSAGES':
            return { ...state, messages: action.payload };
        case 'START_STREAMING':
            return { ...state, isStreaming: true, streamBuffer: '', error: null, isRetrying: false };
        case 'APPEND_TOKEN':
            return { ...state, streamBuffer: state.streamBuffer + action.payload };
        case 'END_STREAMING':
            return { ...state, isStreaming: false, isRetrying: false };
        case 'SET_CLARIFYING_QUESTIONS':
            return {
                ...state,
                phase: 'clarifying',
                clarifyingQuestions: action.payload
            };
        case 'SET_ANSWER':
            return {
                ...state,
                selectedAnswers: { ...state.selectedAnswers, [action.payload.key]: action.payload.value }
            };
        case 'CLEAR_QUESTIONS':
            return {
                ...state,
                phase: 'generating',
                clarifyingQuestions: [],
                selectedAnswers: {}
            };
        case 'SET_GENERATED_POST':
            return {
                ...state,
                phase: 'complete',
                generatedPost: action.payload
            };
        case 'SET_SELECTED_TEMPLATE':
            return {
                ...state,
                selectedTemplate: action.payload
            };
        case 'SET_ERROR':
            return { ...state, error: action.payload, isStreaming: false, isRetrying: false };
        case 'CLEAR_ERROR':
            return { ...state, error: null };
        case 'SET_RETRYING':
            return { ...state, isRetrying: action.payload };
        case 'SET_LAST_REQUEST':
            return { ...state, lastRequest: action.payload };
        case 'RESET':
            return {
                ...state,
                phase: 'idle',
                streamBuffer: '',
                generatedPost: null,
                clarifyingQuestions: [],
                selectedAnswers: {},
                selectedTemplate: null,
                error: null,
                isRetrying: false,
                lastRequest: null
            };
        default:
            return state;
    }
};

// Initial state for AI panel
const initialAIPanelState = {
    messages: [],
    phase: 'idle', // idle | clarifying | generating | complete
    isStreaming: false,
    streamBuffer: '',
    clarifyingQuestions: [],
    selectedAnswers: {},
    generatedPost: null,
    selectedTemplate: null, // Track selected template for Coming Soon
    error: null,
    isRetrying: false,
    lastRequest: null // Store last request for retry
};

// Template wizard state
const initialWizardState = {
    step: 'template', // template | tone | cta | complete
    selectedTemplate: null,
    selectedTone: null,
    selectedCTA: null
};

// Image upload quick actions for Coming Soon posts
const IMAGE_QUICK_ACTIONS = [
    { id: 'generate-poster', label: 'Generate Poster', description: 'Create a Coming Soon poster with this image' },
    { id: 'change-style', label: 'Change Style', description: 'Apply a different visual style' },
    { id: 'adjust-text', label: 'Adjust Text', description: 'Modify text placement or content' },
    { id: 'add-icons', label: 'Add Platform Icons', description: 'Include podcast platform icons' },
    { id: 'refine-composition', label: 'Refine Composition', description: 'Improve layout and balance' },
];

// Error types with recovery options for Coming Soon poster generation
const ERROR_TYPES = {
    TEMPLATE_LOAD_FAILED: {
        type: 'TEMPLATE_LOAD_FAILED',
        title: 'Template Loading Error',
        message: 'Could not load the selected template.',
        recoveryOptions: [
            { id: 'retry', label: 'Try Again', icon: 'refresh' },
            { id: 'default', label: 'Use Default Template', icon: 'image' }
        ]
    },
    IMAGE_GENERATION_FAILED: {
        type: 'IMAGE_GENERATION_FAILED',
        title: 'Generation Failed',
        message: 'The AI could not generate your poster. This may be due to the image complexity or a temporary service issue.',
        recoveryOptions: [
            { id: 'retry', label: 'Try Again', icon: 'refresh' },
            { id: 'simplify', label: 'Simplify Request', icon: 'minimize' },
            { id: 'different-style', label: 'Try Different Style', icon: 'palette' }
        ]
    },
    GUEST_IMAGE_INVALID: {
        type: 'GUEST_IMAGE_INVALID',
        title: 'Invalid Image',
        message: 'The uploaded image could not be processed. Please ensure it\'s a valid image file (JPG, PNG, or WebP) under 10MB.',
        recoveryOptions: [
            { id: 'reupload', label: 'Upload New Image', icon: 'upload' }
        ]
    },
    NETWORK_ERROR: {
        type: 'NETWORK_ERROR',
        title: 'Connection Error',
        message: 'Unable to reach the AI service. Please check your internet connection.',
        recoveryOptions: [
            { id: 'retry', label: 'Retry', icon: 'refresh' }
        ]
    },
    RATE_LIMITED: {
        type: 'RATE_LIMITED',
        title: 'Rate Limited',
        message: 'Too many requests. Please wait a moment before trying again.',
        recoveryOptions: [
            { id: 'wait', label: 'Wait & Retry', icon: 'clock' }
        ]
    },
    UNKNOWN: {
        type: 'UNKNOWN',
        title: 'Something Went Wrong',
        message: 'An unexpected error occurred. Please try again.',
        recoveryOptions: [
            { id: 'retry', label: 'Try Again', icon: 'refresh' },
            { id: 'start-over', label: 'Start Over', icon: 'restart' }
        ]
    }
};

// Helper to classify errors from API responses
const classifyError = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';

    if (errorMessage.includes('template') || errorMessage.includes('style not found')) {
        return ERROR_TYPES.TEMPLATE_LOAD_FAILED;
    }
    if (errorMessage.includes('invalid image') || errorMessage.includes('file type') || errorMessage.includes('too large')) {
        return ERROR_TYPES.GUEST_IMAGE_INVALID;
    }
    if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('connection')) {
        return ERROR_TYPES.NETWORK_ERROR;
    }
    if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests') || error?.status === 429) {
        return ERROR_TYPES.RATE_LIMITED;
    }
    if (errorMessage.includes('generation failed') || errorMessage.includes('no image') || errorMessage.includes('gemini')) {
        return ERROR_TYPES.IMAGE_GENERATION_FAILED;
    }
    return ERROR_TYPES.UNKNOWN;
};

// Error Recovery Component
const ErrorRecovery = ({ error, onRecovery, onDismiss, isRetrying }) => {
    const errorType = classifyError(error);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3"
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle size={16} className="text-red-600" />
                </div>
                <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-800 mb-1">{errorType.title}</h4>
                    <p className="text-xs text-red-600 mb-3">{errorType.message}</p>

                    <div className="flex flex-wrap gap-2">
                        {errorType.recoveryOptions.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => onRecovery(option.id)}
                                disabled={isRetrying}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${option.id === 'retry' || option.id === 'wait'
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : 'bg-white text-red-700 border border-red-200 hover:bg-red-50'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {isRetrying && (option.id === 'retry' || option.id === 'wait') ? (
                                    <>
                                        <RefreshCw size={12} className="animate-spin" />
                                        <span>Retrying...</span>
                                    </>
                                ) : (
                                    <>
                                        {(option.id === 'retry' || option.id === 'wait') && <RefreshCw size={12} />}
                                        <span>{option.label}</span>
                                    </>
                                )}
                            </button>
                        ))}
                        <button
                            onClick={onDismiss}
                            className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// AI Assistant Panel Component with Real Streaming and Targeted Edit Support
const AIAssistantPanel = ({
    platform,
    contentType,
    episode,
    planContent,
    onGeneratedContent,
    onOpenVault,
    currentContent, // New prop: current content from parent for edit detection
    selectedComingSoonTemplate // Selected template from Coming Soon carousel
}) => {
    const [state, dispatch] = useReducer(aiPanelReducer, initialAIPanelState);
    const [inputValue, setInputValue] = useState('');
    const [wizard, setWizard] = useState(initialWizardState);
    const messagesEndRef = useRef(null);
    const isInitializedRef = useRef(false);
    const textareaRef = useRef(null);
    // Use ref to track stream buffer to avoid closure issues in callbacks
    const streamBufferRef = useRef('');
    const onGeneratedContentRef = useRef(onGeneratedContent);

    // Image upload state
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showImageActions, setShowImageActions] = useState(false);
    const fileInputRef = useRef(null);

    // Customization state for Coming Soon posters
    const [showCustomizations, setShowCustomizations] = useState(false);
    const [posterCustomizations, setPosterCustomizations] = useState({
        customText: '',
        textColor: '#FFFFFF',
        backgroundColor: '',
        fontFamily: 'Montserrat, sans-serif'
    });

    // Use the useContentEditor hook for targeted edit support
    const contentEditor = useContentEditor({
        platform,
        context: useMemo(() => assembleContext({ episode, planContent }), [episode, planContent]),
        onContentChange: onGeneratedContent,
        initialContent: currentContent,
        autoValidate: true,
        onValidationWarning: (warnings) => {
            console.log('[AIAssistantPanel] Validation warnings:', warnings);
        }
    });

    // Keep refs updated
    useEffect(() => {
        onGeneratedContentRef.current = onGeneratedContent;
    }, [onGeneratedContent]);

    // Auto-resize textarea based on content
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const maxHeight = 160; // max-h-40 = 10rem = 160px
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, maxHeight) + 'px';
        }
    }, [inputValue]);

    // Update ref when streamBuffer changes
    useEffect(() => {
        streamBufferRef.current = state.streamBuffer;
    }, [state.streamBuffer]);

    // Build context from props AND current generated post for refinement
    const context = useMemo(() => {
        return assembleContext({
            episode,
            planContent,
            currentPost: state.generatedPost || contentEditor.content // Include hook content
        });
    }, [episode, planContent, state.generatedPost, contentEditor.content]);

    // Conversation history for API
    const conversationHistory = useMemo(() => {
        return state.messages.map(msg => ({
            role: msg.isUser ? 'user' : 'assistant',
            content: msg.text
        }));
    }, [state.messages]);

    // Reset wizard when platform changes
    useEffect(() => {
        setWizard(initialWizardState);
        setInputValue('');
    }, [platform]);

    // Auto-scroll to bottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [state.messages, state.streamBuffer, scrollToBottom]);

    // Check if this is a Coming Soon post type - memoized for use in multiple places
    const isComingSoon = useMemo(() => {
        return contentType?.id?.includes('coming-soon') || contentType?.name?.toLowerCase().includes('coming soon');
    }, [contentType]);

    // Initialize with welcome message - only once per component mount
    useEffect(() => {
        // Skip if already initialized or if messages already exist
        if (isInitializedRef.current || state.messages.length > 0) return;
        isInitializedRef.current = true;

        const platformName = contentType?.name || PLATFORMS[platform]?.name || 'social media';

        let welcomeText;
        if (isComingSoon) {
            welcomeText = `Welcome! I'm Mariko, your AI design assistant. I'll help you create a stunning Coming Soon poster for your podcast episode.\n\n**How to get started:**\n1. Browse the template styles in the preview\n2. Click "Select Style" when you find one you like\n3. Upload a guest image using the image icon below\n4. Tell me any adjustments you'd like\n\nLet's create something beautiful!`;
        } else {
            welcomeText = `Hi! I'm Mariko, your AI content assistant. I'll help you create the perfect ${platformName} post from your podcast episode${context.guestName ? ` with ${context.guestName}` : ''}.\n\nJust describe what you'd like, or use the quick actions below to get started.`;
        }

        dispatch({
            type: 'ADD_MESSAGE',
            payload: {
                id: Date.now(),
                text: welcomeText,
                isUser: false
            }
        });
    }, [state.messages.length, isComingSoon, contentType?.name, platform, context.guestName]); // Updated dependencies

    // Use refs for values that change frequently but shouldn't trigger callback recreation
    const inputValueRef = useRef(inputValue);
    const isStreamingRef = useRef(state.isStreaming);
    const phaseRef = useRef(state.phase);
    const selectedAnswersRef = useRef(state.selectedAnswers);
    const contextRef = useRef(context);
    const conversationHistoryRef = useRef(conversationHistory);

    // Keep refs in sync with state
    useEffect(() => {
        inputValueRef.current = inputValue;
        isStreamingRef.current = contentEditor.isStreaming; // Use hook's streaming state
        phaseRef.current = state.phase;
        selectedAnswersRef.current = state.selectedAnswers;
        contextRef.current = context;
        conversationHistoryRef.current = conversationHistory;
    }, [inputValue, contentEditor.isStreaming, state.phase, state.selectedAnswers, context, conversationHistory]);

    // Send message to AI - uses useContentEditor hook for targeted edit support
    const handleSend = useCallback(async () => {
        const currentInputValue = inputValueRef.current;
        if (!currentInputValue.trim() || contentEditor.isStreaming) return;

        const userMessage = { id: Date.now(), text: currentInputValue.trim(), isUser: true };
        dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
        setInputValue('');
        setWizard(initialWizardState); // Reset wizard after sending
        dispatch({ type: 'START_STREAMING' });

        // Build prompt with any selected answers
        let prompt = currentInputValue.trim();
        const currentPhase = phaseRef.current;
        const currentAnswers = selectedAnswersRef.current;

        if (currentPhase === 'clarifying' && Object.keys(currentAnswers).length > 0) {
            const answers = Object.entries(currentAnswers)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n');
            prompt = `${currentInputValue}\n\nMy preferences:\n${answers}`;
            dispatch({ type: 'CLEAR_QUESTIONS' });
        }

        // Check if we're in Coming Soon context (has poster or coming-soon content type)
        const isComingSoonContext = state.generatedPost?.posterUrl ||
            contentType?.id?.includes('coming-soon') ||
            contentType?.name?.toLowerCase().includes('coming soon');

        if (isComingSoonContext) {
            // Use the Coming Soon chat endpoint for AI co-pilot experience
            try {
                const result = await comingSoonService.chat({
                    message: prompt,
                    conversationHistory: state.messages.slice(-10).map(m => ({
                        role: m.isUser ? 'user' : 'assistant',
                        content: m.text
                    })),
                    posterContext: {
                        posterUrl: state.generatedPost?.posterUrl,
                        guestName: context.guestName || episode?.guestName,
                        styleName: selectedComingSoonTemplate?.name || state.generatedPost?.selectedTemplate?.name,
                        styleId: selectedComingSoonTemplate?.id || state.generatedPost?.selectedTemplate?.id,
                        occupation: episode?.guestOccupation || episode?.occupation,
                        guestBio: episode?.guestBio,
                        topic: episode?.topics
                    },
                    guestImage: uploadedImage ? await comingSoonService.fileToBase64(uploadedImage) : null
                });

                dispatch({ type: 'END_STREAMING' });

                // Add the AI's response
                dispatch({
                    type: 'ADD_MESSAGE',
                    payload: {
                        id: Date.now(),
                        text: result.response,
                        isUser: false
                    }
                });

                // If action was taken (poster modified/regenerated), update the preview
                if (result.actionTaken && result.posterUrl) {
                    const updatedPoster = {
                        ...state.generatedPost,
                        posterUrl: result.posterUrl,
                        selectedTemplate: state.generatedPost?.selectedTemplate || { id: 'minimal-premiere' }
                    };

                    dispatch({ type: 'SET_GENERATED_POST', payload: updatedPoster });

                    if (onGeneratedContent) {
                        onGeneratedContent(updatedPoster);
                    }
                }
            } catch (error) {
                dispatch({ type: 'END_STREAMING' });
                dispatch({ type: 'SET_ERROR', payload: error.message });
                dispatch({
                    type: 'ADD_MESSAGE',
                    payload: {
                        id: Date.now(),
                        text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                        isUser: false
                    }
                });
            }
            return;
        }

        // Detect operation mode for user feedback
        const detectedMode = contentEditor.detectMode(prompt);
        console.log('[AIAssistantPanel] Detected mode:', detectedMode);

        // Use the contentEditor hook's processRequest which automatically
        // handles generation vs edit mode based on current content
        try {
            await contentEditor.processRequest(prompt, {
                onToken: (token) => {
                    dispatch({ type: 'APPEND_TOKEN', payload: token });
                },
                onComplete: (result) => {
                    dispatch({ type: 'END_STREAMING' });

                    if (result.content) {
                        // Generation or adjustment — update post and add confirmation message
                        dispatch({ type: 'SET_GENERATED_POST', payload: result.content });

                        const modeMessage = result.mode?.mode === 'edit'
                            ? "I've updated the post with your changes. Check the preview on the left. Would you like any further adjustments?"
                            : "I've created a post for you! Check the preview on the left. Would you like me to make any adjustments?";

                        dispatch({
                            type: 'ADD_MESSAGE',
                            payload: {
                                id: Date.now(),
                                text: modeMessage,
                                isUser: false
                            }
                        });
                    } else if (result.advisoryText) {
                        // Conversational advice — surface the AI's response in chat, no content change
                        dispatch({
                            type: 'ADD_MESSAGE',
                            payload: {
                                id: Date.now(),
                                text: result.advisoryText,
                                isUser: false
                            }
                        });
                    }

                    // Report validation warnings if any
                    if (result.validation?.warnings?.length > 0) {
                        console.warn('[AIAssistantPanel] Edit validation warnings:', result.validation.warnings);
                    }
                },

                onError: (error) => {
                    dispatch({ type: 'END_STREAMING' });
                    dispatch({ type: 'SET_ERROR', payload: error.message });
                    dispatch({
                        type: 'ADD_MESSAGE',
                        payload: {
                            id: Date.now(),
                            text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                            isUser: false
                        }
                    });
                }
            });
        } catch (error) {
            dispatch({ type: 'END_STREAMING' });
            dispatch({ type: 'SET_ERROR', payload: error.message });
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    id: Date.now(),
                    text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                    isUser: false
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentEditor]); // Depend on contentEditor hook - other deps accessed via refs

    // Handle quick action click
    const handleQuickAction = useCallback((action) => {
        setInputValue(action.prompt);
    }, []);

    // Template Wizard Handlers
    const handleTemplateSelect = useCallback((templateKey) => {
        setWizard(prev => ({
            ...prev,
            step: 'tone',
            selectedTemplate: templateKey
        }));
    }, []);

    const handleToneSelect = useCallback((toneId) => {
        setWizard(prev => ({
            ...prev,
            step: 'cta',
            selectedTone: toneId
        }));
    }, []);

    const handleCTASelect = useCallback((ctaId) => {
        setWizard(prev => {
            const newState = {
                ...prev,
                step: 'complete',
                selectedCTA: ctaId
            };

            // Build the final prompt with proper formatting
            const templates = PLATFORM_TEMPLATES[platform] || LINKEDIN_TEMPLATES;
            const template = templates[prev.selectedTemplate];
            const toneOptions = PLATFORM_TONE_OPTIONS[platform] || TONE_OPTIONS;
            const ctaOptions = PLATFORM_CTA_OPTIONS[platform] || CTA_OPTIONS;
            const tone = toneOptions.find(t => t.id === prev.selectedTone);
            const cta = ctaOptions.find(c => c.id === ctaId);

            // Build a coherent, well-formatted prompt
            const promptLines = [];

            // Main instruction from template
            promptLines.push(template?.prompt || 'Create a social media post from this podcast episode.');

            // Add tone guidance
            if (tone) {
                promptLines.push(`Write in a ${tone.label.toLowerCase()} tone${tone.description ? ` - ${tone.description.toLowerCase()}` : ''}.`);
            }

            // Add CTA instruction
            if (cta) {
                promptLines.push(`Include a call-to-action at the end: ${cta.prompt}`);
            }

            // Add platform-specific guidance
            if (platform === 'twitter') {
                promptLines.push('Keep the post under 280 characters and make it engaging for Twitter/X.');
            } else if (platform === 'linkedin') {
                promptLines.push('Format the post professionally with appropriate line breaks for LinkedIn.');
            } else if (platform === 'facebook') {
                promptLines.push('Format the post for Facebook with engaging content, emojis, and relevant hashtags.');
            }

            const finalPrompt = promptLines.join('\n\n');
            setInputValue(finalPrompt);

            return newState;
        });
    }, [platform]);

    const handleWizardBack = useCallback(() => {
        setWizard(prev => {
            if (prev.step === 'tone') {
                return { ...prev, step: 'template', selectedTemplate: null };
            } else if (prev.step === 'cta') {
                return { ...prev, step: 'tone', selectedTone: null };
            } else if (prev.step === 'complete') {
                return { ...prev, step: 'cta', selectedCTA: null };
            }
            return prev;
        });
    }, []);

    // Image upload handlers
    const handleImageUpload = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    id: Date.now(),
                    text: "Please upload a valid image file (JPG, PNG, or WebP).",
                    isUser: false
                }
            });
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    id: Date.now(),
                    text: "The image is too large. Please upload an image smaller than 10MB.",
                    isUser: false
                }
            });
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (event) => {
            setImagePreview(event.target.result);
            setUploadedImage(file);
            setShowImageActions(true);

            // Add message about uploaded image
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    id: Date.now(),
                    text: `I've received your image "${file.name}". What would you like me to do with it?`,
                    isUser: false
                }
            });
        };
        reader.readAsDataURL(file);

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    // Core poster generation function - reusable for retry
    const generatePoster = useCallback(async (requestParams) => {
        const { styleId, guestImage, posterContext } = requestParams;

        const result = await comingSoonService.generatePoster({
            styleId,
            guestImage,
            context: posterContext
        });

        if (!result.success || !result.posterUrl) {
            throw new Error(result.error || 'Failed to generate poster');
        }

        return result;
    }, []);

    // Error recovery handler
    const handleRecovery = useCallback(async (recoveryOption) => {
        dispatch({ type: 'CLEAR_ERROR' });

        switch (recoveryOption) {
            case 'retry':
            case 'wait':
                if (state.lastRequest) {
                    dispatch({ type: 'SET_RETRYING', payload: true });

                    dispatch({
                        type: 'ADD_MESSAGE',
                        payload: {
                            id: Date.now(),
                            text: "Retrying your poster generation...",
                            isUser: false
                        }
                    });

                    try {
                        const result = await generatePoster(state.lastRequest);

                        dispatch({ type: 'END_STREAMING' });

                        const posterContent = {
                            posterUrl: result.posterUrl,
                            guestName: state.lastRequest.posterContext.guestName,
                            topic: state.lastRequest.posterContext.topic,
                            caption: result.caption,
                            selectedTemplate: { id: state.lastRequest.styleId }
                        };

                        dispatch({ type: 'SET_GENERATED_POST', payload: posterContent });

                        if (onGeneratedContent) {
                            onGeneratedContent(posterContent);
                        }

                        dispatch({
                            type: 'ADD_MESSAGE',
                            payload: {
                                id: Date.now(),
                                text: "I've created your Coming Soon poster! Check the preview on the left. Would you like me to make any adjustments?",
                                isUser: false
                            }
                        });
                    } catch (error) {
                        dispatch({ type: 'SET_ERROR', payload: error });
                    }
                }
                break;

            case 'default':
                // Retry with default template
                if (state.lastRequest) {
                    dispatch({ type: 'SET_RETRYING', payload: true });

                    const defaultRequest = {
                        ...state.lastRequest,
                        styleId: 'minimal-premiere'
                    };

                    try {
                        const result = await generatePoster(defaultRequest);

                        dispatch({ type: 'END_STREAMING' });

                        const posterContent = {
                            posterUrl: result.posterUrl,
                            guestName: defaultRequest.posterContext.guestName,
                            topic: defaultRequest.posterContext.topic,
                            caption: result.caption,
                            selectedTemplate: { id: 'minimal-premiere' }
                        };

                        dispatch({ type: 'SET_GENERATED_POST', payload: posterContent });

                        if (onGeneratedContent) {
                            onGeneratedContent(posterContent);
                        }

                        dispatch({
                            type: 'ADD_MESSAGE',
                            payload: {
                                id: Date.now(),
                                text: "I've created your poster using the default Minimal Premiere style. Would you like to try a different style?",
                                isUser: false
                            }
                        });
                    } catch (error) {
                        dispatch({ type: 'SET_ERROR', payload: error });
                    }
                }
                break;

            case 'reupload':
                fileInputRef.current?.click();
                break;

            case 'different-style':
                // Clear error and show template carousel
                dispatch({ type: 'CLEAR_ERROR' });
                dispatch({
                    type: 'ADD_MESSAGE',
                    payload: {
                        id: Date.now(),
                        text: "Please select a different style from the carousel above, then upload your image again.",
                        isUser: false
                    }
                });
                break;

            case 'start-over':
                dispatch({ type: 'RESET' });
                setUploadedImage(null);
                setImagePreview(null);
                setShowImageActions(false);
                break;

            default:
                dispatch({ type: 'CLEAR_ERROR' });
        }
    }, [state.lastRequest, generatePoster, onGeneratedContent]);

    const handleImageAction = useCallback(async (action) => {
        if (!uploadedImage) return;

        // For generate-poster action, call the Coming Soon service directly
        if (action.id === 'generate-poster') {
            setShowImageActions(false);
            dispatch({ type: 'START_STREAMING' });

            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    id: Date.now(),
                    text: "I'm generating your Coming Soon poster now. This may take a moment...",
                    isUser: false
                }
            });

            try {
                // Convert image to base64
                const guestImage = await comingSoonService.fileToBase64(uploadedImage);

                // Get the current template from the carousel (passed from parent, default to minimal-premiere)
                const styleId = selectedComingSoonTemplate?.id || 'minimal-premiere';
                console.log('[AIAssistantPanel] Using styleId:', styleId, 'from template:', selectedComingSoonTemplate);

                // Build context from episode data
                const posterContext = {
                    guestName: context.guestName || episode?.guestName || 'Guest',
                    occupation: episode?.guestOccupation || episode?.occupation || '',
                    guestBio: context.guestBio || episode?.guestBio || '',
                    topic: context.topics || episode?.topics || 'Podcast Episode'
                };

                // Store request for potential retry
                const requestParams = {
                    styleId,
                    guestImage,
                    posterContext,
                    options: {
                        customizations: posterCustomizations
                    }
                };
                dispatch({ type: 'SET_LAST_REQUEST', payload: requestParams });

                const result = await generatePoster(requestParams);

                dispatch({ type: 'END_STREAMING' });

                // Update the preview with the generated poster
                const posterContent = {
                    posterUrl: result.posterUrl,
                    guestName: posterContext.guestName,
                    topic: posterContext.topic,
                    caption: result.caption,
                    selectedTemplate: { id: styleId }
                };

                dispatch({ type: 'SET_GENERATED_POST', payload: posterContent });

                if (onGeneratedContent) {
                    onGeneratedContent(posterContent);
                }

                dispatch({
                    type: 'ADD_MESSAGE',
                    payload: {
                        id: Date.now(),
                        text: "I've created your Coming Soon poster! Check the preview on the left. Would you like me to make any adjustments to the style, text, or composition?",
                        isUser: false
                    }
                });
            } catch (error) {
                dispatch({ type: 'END_STREAMING' });
                dispatch({ type: 'SET_ERROR', payload: error });
            }
            return;
        }

        // For other actions, set the prompt for text-based refinement
        const actionPrompts = {
            'change-style': 'Apply a different visual style to this poster. Suggest alternative design approaches.',
            'adjust-text': 'Adjust the text on this poster. Improve the typography and text placement.',
            'add-icons': 'Add podcast platform icons (Apple Podcasts, Spotify, YouTube) to this poster in a minimal, premium way.',
            'refine-composition': 'Refine the composition of this poster. Improve the layout and visual balance.'
        };

        const prompt = actionPrompts[action.id] || action.description;
        setInputValue(prompt);
        setShowImageActions(false);
    }, [uploadedImage, context, episode, selectedComingSoonTemplate, onGeneratedContent, generatePoster]);

    const handleRemoveImage = useCallback(() => {
        setUploadedImage(null);
        setImagePreview(null);
        setShowImageActions(false);
        setShowCustomizations(false);
    }, []);

    const triggerImageUpload = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleWizardReset = useCallback(() => {
        setWizard(initialWizardState);
        setInputValue('');
    }, []);

    // Handle answer selection for clarifying questions
    const handleAnswerSelect = useCallback((question, answer) => {
        dispatch({
            type: 'SET_ANSWER',
            payload: { key: question, value: answer }
        });
    }, []);

    // Handle key press
    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    return (
        <div className="flex flex-col h-full bg-gradient-to-b from-slate-50 to-white">
            {/* Header */}
            <div className="p-4 border-b border-slate-100 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-indigo-200/50 ${state.isStreaming ? 'animate-pulse' : ''}`}>
                                <Sparkles size={18} className="text-white" />
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${state.isStreaming ? 'bg-yellow-500' : 'bg-green-500'}`} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 font-['Outfit']">Mariko AI</h3>
                            <div className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${state.isStreaming ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`} />
                                <span className="text-[10px] text-gray-500">
                                    {state.isStreaming ? 'Generating...' : 'Ready to assist'}
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* Vault Button */}
                    <button
                        onClick={onOpenVault}
                        className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg text-xs font-medium text-indigo-700 hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 transition-all"
                    >
                        <Database size={14} />
                        Vault
                    </button>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1">
                {state.messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg.text} isUser={msg.isUser} />
                ))}

                {/* Streaming content */}
                {state.isStreaming && state.streamBuffer && (
                    <MessageBubble message={state.streamBuffer} isUser={false} isStreaming={true} activeTool={contentEditor.activeTool} />
                )}

                {/* Typing indicator */}
                {state.isStreaming && !state.streamBuffer && (
                    <MessageBubble isTyping={true} isUser={false} activeTool={contentEditor.activeTool} />
                )}

                {/* Clarifying Questions UI */}
                {state.phase === 'clarifying' && state.clarifyingQuestions.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100"
                    >
                        <p className="text-xs font-semibold text-indigo-700 mb-2">Quick answers:</p>
                        <div className="space-y-2">
                            {state.clarifyingQuestions.map((question, idx) => (
                                <div key={idx} className="flex flex-wrap gap-1.5">
                                    <span className="text-xs text-gray-600 w-full mb-1">{question}</span>
                                    {['Professional', 'Casual', 'Thought-provoking'].map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => handleAnswerSelect(question, option)}
                                            className={`px-2 py-1 text-[10px] rounded-full transition-all ${state.selectedAnswers[question] === option
                                                ? 'bg-indigo-500 text-white'
                                                : 'bg-white text-gray-600 hover:bg-indigo-100 border border-gray-200'
                                                }`}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Error Recovery UI */}
                <AnimatePresence>
                    {state.error && (
                        <ErrorRecovery
                            error={state.error}
                            onRecovery={handleRecovery}
                            onDismiss={() => dispatch({ type: 'CLEAR_ERROR' })}
                            isRetrying={state.isRetrying}
                        />
                    )}
                </AnimatePresence>

                <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions - Show template wizard when no post, refinement actions when post exists */}
            {/* Hide template wizard for Coming Soon posts - they use the visual style carousel instead */}
            <div className="px-4 pb-2">
                {/* Template Wizard for initial generation - NOT for Coming Soon */}
                {!state.generatedPost && state.phase === 'idle' && !isComingSoon && (
                    <div className="mb-2 overflow-hidden">
                        {/* Step 1: Template Selection */}
                        <AnimatePresence mode="wait">
                            {wizard.step === 'template' && (
                                <motion.div
                                    key="template"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Start with a template:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(PLATFORM_TEMPLATES[platform] || LINKEDIN_TEMPLATES).map(([key, template]) => (
                                            <button
                                                key={key}
                                                onClick={() => handleTemplateSelect(key)}
                                                disabled={state.isStreaming}
                                                className="px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-full text-xs font-medium text-indigo-700 hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {template.name}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 2: Tone Selection */}
                            {wizard.step === 'tone' && (
                                <motion.div
                                    key="tone"
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={handleWizardBack}
                                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                            >
                                                <ChevronLeft size={14} className="text-gray-500" />
                                            </button>
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                Choose a tone:
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleWizardReset}
                                            className="text-[10px] text-gray-400 hover:text-gray-600"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {(PLATFORM_TONE_OPTIONS[platform] || TONE_OPTIONS).map((tone) => (
                                            <button
                                                key={tone.id}
                                                onClick={() => handleToneSelect(tone.id)}
                                                disabled={state.isStreaming}
                                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${wizard.selectedTone === tone.id
                                                    ? 'bg-indigo-500 text-white border border-indigo-500'
                                                    : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                                                    }`}
                                            >
                                                {tone.label}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 3: CTA Selection */}
                            {wizard.step === 'cta' && (
                                <motion.div
                                    key="cta"
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={handleWizardBack}
                                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                            >
                                                <ChevronLeft size={14} className="text-gray-500" />
                                            </button>
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                Add a call-to-action:
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleWizardReset}
                                            className="text-[10px] text-gray-400 hover:text-gray-600"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {(PLATFORM_CTA_OPTIONS[platform] || CTA_OPTIONS).map((cta) => (
                                            <button
                                                key={cta.id}
                                                onClick={() => handleCTASelect(cta.id)}
                                                disabled={state.isStreaming}
                                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${wizard.selectedCTA === cta.id
                                                    ? 'bg-indigo-500 text-white border border-indigo-500'
                                                    : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                                                    }`}
                                            >
                                                {cta.label}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 4: Complete - Show selected options summary */}
                            {wizard.step === 'complete' && (
                                <motion.div
                                    key="complete"
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={handleWizardBack}
                                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                            >
                                                <ChevronLeft size={14} className="text-gray-500" />
                                            </button>
                                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                Ready to generate!
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleWizardReset}
                                            className="text-[10px] text-gray-400 hover:text-gray-600"
                                        >
                                            Start over
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Your prompt is ready below. Press send or customize it further.
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {/* Refinement actions when post exists - NOT for Coming Soon */}
                {state.generatedPost && !isComingSoon && (
                    <div className="flex flex-wrap gap-2">
                        {(PLATFORM_QUICK_ACTIONS[platform] || QUICK_ACTIONS).map((action) => (
                            <button
                                key={action.id}
                                onClick={() => handleQuickAction(action)}
                                disabled={state.isStreaming}
                                className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-slate-100 bg-white/80 backdrop-blur-sm">
                {/* Hidden file input for image upload */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                />

                {/* Image Preview */}
                <AnimatePresence>
                    {imagePreview && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mb-3"
                        >
                            <div className="relative inline-block">
                                <img
                                    src={imagePreview}
                                    alt="Uploaded preview"
                                    className="h-20 w-20 object-cover rounded-lg border border-gray-200 shadow-sm"
                                />
                                <button
                                    onClick={handleRemoveImage}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                                >
                                    <X size={12} className="text-white" />
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Image Quick Actions */}
                <AnimatePresence>
                    {showImageActions && uploadedImage && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="mb-3 p-3 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100"
                        >
                            <p className="text-xs font-medium text-purple-700 mb-2">What would you like to do with this image?</p>
                            <div className="flex flex-wrap gap-2">
                                {IMAGE_QUICK_ACTIONS.map((action) => (
                                    <button
                                        key={action.id}
                                        onClick={() => handleImageAction(action)}
                                        className="px-3 py-1.5 bg-white border border-purple-200 rounded-full text-xs font-medium text-purple-600 hover:bg-purple-100 hover:border-purple-300 transition-all"
                                    >
                                        {action.label}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setShowCustomizations(!showCustomizations)}
                                    className="px-3 py-1.5 bg-white border border-purple-200 rounded-full text-xs font-medium text-purple-600 hover:bg-purple-100 hover:border-purple-300 transition-all ml-auto"
                                >
                                    {showCustomizations ? 'Hide Customizations' : 'Customize Colors & Text'}
                                </button>
                            </div>

                            {/* Customization Controls */}
                            <AnimatePresence>
                                {showCustomizations && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-3 pt-3 border-t border-purple-100 overflow-hidden"
                                    >
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[10px] font-semibold text-purple-700 uppercase tracking-wider mb-1">Custom Text</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. OUT NOW"
                                                    value={posterCustomizations.customText}
                                                    onChange={(e) => setPosterCustomizations(prev => ({ ...prev, customText: e.target.value }))}
                                                    className="w-full text-xs px-2 py-1.5 border border-purple-200 rounded-md bg-white focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-semibold text-purple-700 uppercase tracking-wider mb-1">Font Family</label>
                                                <select
                                                    value={posterCustomizations.fontFamily}
                                                    onChange={(e) => setPosterCustomizations(prev => ({ ...prev, fontFamily: e.target.value }))}
                                                    className="w-full text-xs px-2 py-1.5 border border-purple-200 rounded-md bg-white focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                                                >
                                                    <option value="Montserrat, sans-serif">Montserrat</option>
                                                    <option value="Playfair Display, serif">Playfair Display</option>
                                                    <option value="Inter, sans-serif">Inter</option>
                                                    <option value="Oswald, sans-serif">Oswald</option>
                                                    <option value="Bebas Neue, sans-serif">Bebas Neue</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-semibold text-purple-700 uppercase tracking-wider mb-1">Text Color</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={posterCustomizations.textColor}
                                                        onChange={(e) => setPosterCustomizations(prev => ({ ...prev, textColor: e.target.value }))}
                                                        className="h-6 w-6 rounded border border-purple-200 cursor-pointer p-0"
                                                    />
                                                    <span className="text-[10px] bg-white px-2 py-1 rounded border border-purple-100 uppercase font-mono">{posterCustomizations.textColor}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-semibold text-purple-700 uppercase tracking-wider mb-1">Brand BG Color <span className="text-purple-400 lowercase normal-case font-normal">(optional)</span></label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={posterCustomizations.backgroundColor || '#000000'}
                                                        onChange={(e) => setPosterCustomizations(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                                        disabled={!posterCustomizations.backgroundColor}
                                                        className="h-6 w-6 rounded border border-purple-200 cursor-pointer p-0 disabled:opacity-50"
                                                    />
                                                    <button
                                                        onClick={() => setPosterCustomizations(prev => ({ ...prev, backgroundColor: prev.backgroundColor ? '' : '#000000' }))}
                                                        className={`text-[10px] px-2 py-1 rounded border transition-colors ${posterCustomizations.backgroundColor ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'bg-white text-purple-600 border-purple-200 hover:bg-purple-50'}`}
                                                    >
                                                        {posterCustomizations.backgroundColor ? 'Remove' : 'Add BG Color'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="flex items-start gap-2">
                    {/* Image Upload Button */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={triggerImageUpload}
                        disabled={state.isStreaming}
                        className="w-11 h-11 bg-slate-100 hover:bg-slate-200 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 border border-slate-200"
                        title="Upload image"
                    >
                        <Image size={18} className="text-slate-600" />
                    </motion.button>

                    <div className="flex-1 relative">
                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder={state.phase === 'clarifying'
                                ? "Answer the questions or type your preferences..."
                                : "Ask Mariko to create or adjust your post..."}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-gray-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all resize-none overflow-y-auto max-h-40"
                            disabled={state.isStreaming}
                            rows={1}
                        />
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleSend}
                        disabled={!inputValue.trim() || state.isStreaming}
                        className="w-11 h-11 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200/50 hover:shadow-indigo-300/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                        {state.isStreaming ? (
                            <Loader2 size={18} className="text-white animate-spin" />
                        ) : (
                            <Send size={18} className="text-white" />
                        )}
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

// Main Content Creation Modal Component
const ContentCreationModal = ({
    isOpen,
    onClose,
    initialPlatform = 'instagram',
    episode = null,
    contentType = null, // The content type being generated (from ContentTypeCard)
    planContent = null, // Full markdown content from SmartEditor
    onGenerate
}) => {
    // Determine the initial platform from contentType or initialPlatform prop
    const getInitialPlatform = useCallback(() => {
        // Priority: contentType.id > initialPlatform > 'instagram'
        if (contentType?.id === 'show-notes') return 'show-notes';
        if (contentType?.id === 'blog-post') return 'blog-post';
        if (contentType?.id === 'email-newsletter') return 'email-newsletter';
        return initialPlatform || 'instagram';
    }, [contentType?.id, initialPlatform]);

    const [activePlatform, setActivePlatform] = useState(getInitialPlatform);
    const [generatedContent, setGeneratedContent] = useState(null);
    const [editedContent, setEditedContent] = useState(null); // Track user edits
    const [savedContent, setSavedContent] = useState(null); // Track saved state

    // Separate state for Coming Soon template selection (independent of generatedContent)
    const [selectedComingSoonTemplate, setSelectedComingSoonTemplate] = useState(null);

    // Media attachment state for posts
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);

    // Save to Vault state
    const [isSavingToVault, setIsSavingToVault] = useState(false);

    // Vault drawer state - lifted from AIAssistantPanel to render at app level
    const [isVaultOpen, setIsVaultOpen] = useState(false);

    // Reset platform and content when modal opens with new contentType
    const prevContentTypeIdRef = useRef(null);
    useEffect(() => {
        const currentContentTypeId = contentType?.id;
        // Only reset if contentType actually changed (not on every render)
        if (prevContentTypeIdRef.current !== currentContentTypeId) {
            prevContentTypeIdRef.current = currentContentTypeId;
            const newPlatform = getInitialPlatform();
            setActivePlatform(newPlatform);
            // Reset content when contentType changes
            setGeneratedContent(null);
            setEditedContent(null);
            setSavedContent(null);
            setSelectedMedia(null);
            setSelectedComingSoonTemplate(null);
        }
    }, [contentType?.id, getInitialPlatform]);

    // Reset content when platform changes to keep platforms separate
    useEffect(() => {
        setGeneratedContent(null);
        setEditedContent(null);
        setSavedContent(null);
        setSelectedMedia(null);
        setSelectedComingSoonTemplate(null); // Reset template selection on platform change
    }, [activePlatform]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (!editedContent) return false;
        return editedContent !== savedContent;
    }, [editedContent, savedContent]);

    // Handle content edit from preview
    const handleContentChange = useCallback((newContent) => {
        setEditedContent(newContent);
    }, []);

    // Handle save from preview
    const handleSaveContent = useCallback(() => {
        if (editedContent) {
            setGeneratedContent(editedContent);
            setSavedContent(editedContent);
        }
    }, [editedContent]);

    // Handle Coming Soon template selection from carousel
    const handleComingSoonTemplateSelect = useCallback((template) => {
        console.log('[ContentCreationModal] Template selected:', template);
        // Store the selected template in separate state
        setSelectedComingSoonTemplate(template);
    }, []);

    // Handle Save to Vault - saves both text and image with platform-specific organization
    const handleSaveToVault = useCallback(async () => {
        if (!editedContent) return false;

        setIsSavingToVault(true);
        try {
            // Generate title from content (first line, truncated)
            const firstLine = editedContent.split('\n')[0];
            const platformName = activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1);
            const title = `${platformName} Post: ${firstLine.slice(0, 50)}${firstLine.length > 50 ? '...' : ''}`;

            // Platform-specific folder for organization
            const folder = platformName; // e.g., 'LinkedIn', 'Twitter'

            // Platform-specific tags for organization
            const platformTags = [activePlatform, 'social-media', 'content-creation'];
            const description = `${platformName} post created in Content Creation Studio`;

            // Save text content to vault with folder
            const textResult = await vaultService.uploadTextSnippet(
                editedContent,
                title,
                episode?.id,
                platformTags,
                description,
                folder // Save to platform-specific folder
            );

            if (textResult.success) {
                // Mark as saved
                setSavedContent(editedContent);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to save to vault:', error);
            return false;
        } finally {
            setIsSavingToVault(false);
        }
    }, [editedContent, activePlatform, episode?.id]);

    // Handle Start Over - Reset modal to initial state
    const handleStartOver = useCallback(() => {
        setGeneratedContent(null);
        setEditedContent(null);
        setSavedContent(null);
        setSelectedMedia(null);
    }, []);

    // Handle media upload for LinkedIn posts
    // Supports both File objects (from file input/OS drag) and media objects (from vault drag)
    const handleMediaChange = useCallback(async (fileOrMedia) => {
        if (!fileOrMedia) {
            setSelectedMedia(null);
            return;
        }

        // Check if this is already a media object (from vault drag-drop)
        // Media objects have a url property, File objects don't
        if (fileOrMedia.url && typeof fileOrMedia.url === 'string') {
            // This is a media object from vault - just set it directly
            setSelectedMedia({
                url: fileOrMedia.url,
                name: fileOrMedia.name,
                type: fileOrMedia.type,
                size: fileOrMedia.size,
                id: fileOrMedia.id
            });
            return;
        }

        // This is a File object - need to upload it
        setIsUploadingMedia(true);
        try {
            const platformName = activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1);
            const result = await vaultService.uploadFile(fileOrMedia, {
                episodeId: episode?.id,
                tags: [activePlatform, 'social-media', 'content-creation'],
                description: `${platformName} post image for ${episode?.title || 'content'}`,
                folder: platformName // Save to platform-specific folder
            });

            // The vault service returns { success: true, asset: { downloadUrl, id, ... } }
            const asset = result.asset || result;

            // Set the uploaded media with its URL
            setSelectedMedia({
                url: asset.downloadUrl || asset.url || asset.fileUrl,
                name: fileOrMedia.name,
                type: fileOrMedia.type,
                size: fileOrMedia.size,
                id: asset.id || asset.assetId
            });
        } catch (error) {
            console.error('Failed to upload media:', error);
            throw error; // Re-throw to let the LinkedInPreview handle the error
        } finally {
            setIsUploadingMedia(false);
        }
    }, [episode?.id, episode?.title, activePlatform]);

    // Sync edited content when generated content changes
    useEffect(() => {
        if (generatedContent) {
            setEditedContent(generatedContent);
            setSavedContent(generatedContent);
        }
    }, [generatedContent]);

    // Determine Instagram content type (reels vs post)
    const instagramContentType = useMemo(() => {
        return getInstagramContentType(contentType);
    }, [contentType]);

    // Check if this is Instagram Reels content
    const isInstagramReels = instagramContentType === 'reels';

    // Check if this is any Instagram content
    const isInstagramContent = contentType?.platform === 'Instagram' ||
        activePlatform === 'instagram' ||
        instagramContentType !== null;

    // Check if frameless (IG, TikTok, YouTube Shorts, LinkedIn, Twitter, Facebook, Show Notes)
    // Instagram Coming Soon should also be frameless (using the card style)
    // IMPORTANT: Only use activePlatform for checks to ensure correct preview when switching platforms
    const isFramelessPlatform = isInstagramContent || activePlatform === 'tiktok' || activePlatform === 'youtube' || activePlatform === 'linkedin' || activePlatform === 'twitter' || activePlatform === 'facebook' || activePlatform === 'show-notes' || activePlatform === 'blog-post' || activePlatform === 'email-newsletter';

    // YouTube Thumbnail State
    const [selectedThumbnailTemplate, setSelectedThumbnailTemplate] = useState(0);
    const [thumbnailHeadshots, setThumbnailHeadshots] = useState({ user: null, guest: null });

    // Detect if this is YouTube Thumbnail content
    const isYouTubeThumbnail = useMemo(() => {
        const id = contentType?.id?.toLowerCase() || '';
        const name = contentType?.name?.toLowerCase() || '';
        return id.includes('thumbnail') || name.includes('thumbnail');
    }, [contentType]);

    // Handlers for YouTube Thumbnail
    const handleThumbnailTemplateChange = (templateIndex) => {
        setSelectedThumbnailTemplate(templateIndex);
    };

    const handleHeadshotsChange = (newHeadshots) => {
        setThumbnailHeadshots(newHeadshots);
    };

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        variants={overlayVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={onClose}
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
                    />

                    {/* Modal */}
                    <motion.div
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="relative w-full max-w-6xl h-[85vh] bg-white/90 backdrop-blur-2xl border border-white/60 shadow-[0_32px_64px_-16px_rgba(79,70,229,0.3)] rounded-[32px] overflow-hidden flex flex-col"
                    >
                        {/* Background Atmospherics */}
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                            <div className="absolute top-[-30%] left-[-10%] w-[60%] h-[60%] bg-gradient-to-b from-indigo-100/40 to-transparent blur-3xl opacity-50" />
                            <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-t from-purple-100/30 to-transparent blur-3xl opacity-50" />
                            <div className="absolute top-[20%] right-[30%] w-[30%] h-[30%] bg-gradient-to-b from-pink-100/20 to-transparent blur-2xl opacity-40" />
                        </div>

                        {/* Header */}
                        <div className="relative z-10 flex items-center justify-between p-5 border-b border-slate-100/50 bg-white/50 backdrop-blur-sm">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
                                    <Sparkles size={18} className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">
                                        {contentType?.name || 'Content Creation Studio'}
                                    </h2>
                                    <p className="text-xs text-gray-500">
                                        {contentType?.platform && (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${contentType.platform === 'Instagram' ? 'bg-pink-100 text-pink-700' :
                                                    contentType.platform === 'TikTok' ? 'bg-gray-100 text-gray-700' :
                                                        contentType.platform === 'LinkedIn' ? 'bg-blue-100 text-blue-700' :
                                                            contentType.platform === 'X / Twitter' ? 'bg-gray-100 text-gray-700' :
                                                                contentType.platform === 'YouTube' ? 'bg-red-100 text-red-700' :
                                                                    'bg-indigo-100 text-indigo-700'
                                                    }`}>
                                                    {contentType.platform}
                                                </span>
                                                {contentType.aspectRatio && (
                                                    <span className="text-gray-400">· {contentType.aspectRatio}</span>
                                                )}
                                                {contentType.duration && (
                                                    <span className="text-gray-400">· {contentType.duration}</span>
                                                )}
                                            </span>
                                        )}
                                        {!contentType?.platform && (episode?.episodeTitle || episode?.title || 'Create platform-optimized content')}
                                    </p>
                                </div>
                            </div>



                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl hover:bg-slate-100 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content Area - Two Pane Layout */}
                        <div className="relative z-10 flex flex-1 overflow-hidden">
                            {isYouTubeThumbnail ? (
                                /* YouTube Thumbnail Mode - Two Pane Layout */
                                <>
                                    {/* Left Pane - Thumbnail Preview */}
                                    <div className="w-1/2 p-6 border-r border-slate-100/50">
                                        <div className="h-full flex flex-col min-h-0">
                                            <div className="flex items-center justify-between mb-4 flex-none">
                                                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500" />
                                                    YouTube Thumbnail
                                                    <span className="text-[10px] font-medium text-gray-500 bg-red-50 px-2 py-0.5 rounded-full ml-1">
                                                        16:9 • 1280x720
                                                    </span>
                                                </h3>
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <Clock size={12} />
                                                    <span>Live preview</span>
                                                </div>
                                            </div>

                                            {/* YouTube Thumbnail Preview with Carousel */}
                                            <div className="flex-1 min-h-0">
                                                <YouTubeThumbnailPreview
                                                    selectedTemplate={selectedThumbnailTemplate}
                                                    headshots={thumbnailHeadshots}
                                                    onTemplateChange={handleThumbnailTemplateChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Pane - Thumbnail Customization Panel */}
                                    <div className="w-1/2 flex flex-col">
                                        <ThumbnailCustomizationPanel
                                            headshots={thumbnailHeadshots}
                                            onHeadshotsChange={handleHeadshotsChange}
                                        />
                                    </div>
                                </>
                            ) : (
                                /* Standard Platform Preview Mode */
                                <>
                                    {/* Left Pane - Platform Preview */}
                                    <div className="w-1/2 p-6 border-r border-slate-100/50">
                                        <div className="h-full flex flex-col min-h-0">
                                            <div className="flex items-center justify-between mb-4 flex-none">
                                                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${PLATFORMS[activePlatform].color}`} />
                                                    {PLATFORMS[activePlatform].name} Preview
                                                    {isInstagramContent && instagramContentType && (
                                                        <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-1">
                                                            {instagramContentType === 'reels' ? 'Reels' : 'Post'}
                                                        </span>
                                                    )}
                                                </h3>
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <Clock size={12} />
                                                    <span>Live preview</span>
                                                </div>
                                            </div>

                                            {/* Preview Container - No phone frame for Instagram content or TikTok or YouTube */}
                                            {isFramelessPlatform ? (
                                                /* Raw Social Media Interface - No Phone Frame */
                                                <div className="flex-1 flex items-center justify-center min-h-0">
                                                    {isInstagramReels || activePlatform === 'tiktok' || activePlatform === 'youtube' ? (
                                                        /* Vertical Video (Reels/TikTok/Shorts) - 9:16 Aspect Ratio */
                                                        <div className="relative aspect-[9/16] h-full max-h-full w-auto max-w-full rounded-[2rem] overflow-hidden shadow-2xl border border-gray-800 bg-black">
                                                            <PlatformPreview
                                                                platform={activePlatform}
                                                                instagramContentType={instagramContentType}
                                                                contentType={contentType}
                                                                generatedContent={generatedContent}
                                                                guestName={episode?.guestName || episode?.founderName}
                                                                onContentChange={handleContentChange}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={handleSaveContent}
                                                                onComingSoonTemplateSelect={handleComingSoonTemplateSelect}
                                                                selectedComingSoonTemplate={selectedComingSoonTemplate}
                                                            />
                                                        </div>
                                                    ) : activePlatform === 'linkedin' || activePlatform === 'twitter' || activePlatform === 'facebook' || activePlatform === 'show-notes' ? (
                                                        /* LinkedIn/Twitter/Facebook/Show Notes - Desktop Feed Style (No separate window container) */
                                                        <div className="relative w-full max-w-[550px] h-full rounded-xl overflow-hidden">
                                                            <PlatformPreview
                                                                platform={activePlatform}
                                                                instagramContentType={instagramContentType}
                                                                contentType={contentType}
                                                                generatedContent={editedContent || generatedContent}
                                                                guestName={episode?.guestName || episode?.founderName}
                                                                onContentChange={handleContentChange}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={handleSaveContent}
                                                                media={selectedMedia}
                                                                onMediaChange={handleMediaChange}
                                                                isUploading={isUploadingMedia}
                                                                episode={episode}
                                                                onSaveToVault={handleSaveToVault}
                                                                onStartOver={handleStartOver}
                                                                isSavingToVault={isSavingToVault}
                                                                onComingSoonTemplateSelect={handleComingSoonTemplateSelect}
                                                                selectedComingSoonTemplate={selectedComingSoonTemplate}
                                                            />
                                                        </div>
                                                    ) : activePlatform === 'blog-post' ? (
                                                        /* Blog Post - Wider Desktop Browser Style */
                                                        <div className="relative w-full h-full rounded-xl overflow-hidden">
                                                            <PlatformPreview
                                                                platform={activePlatform}
                                                                contentType={contentType}
                                                                generatedContent={editedContent || generatedContent}
                                                                onContentChange={handleContentChange}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={handleSaveContent}
                                                                onSaveToVault={handleSaveToVault}
                                                                onStartOver={handleStartOver}
                                                                isSavingToVault={isSavingToVault}
                                                            />
                                                        </div>
                                                    ) : activePlatform === 'email-newsletter' ? (
                                                        /* Email Newsletter - Email Client Style */
                                                        <div className="relative w-full h-full rounded-xl overflow-hidden">
                                                            <PlatformPreview
                                                                platform={activePlatform}
                                                                contentType={contentType}
                                                                generatedContent={editedContent || generatedContent}
                                                                onContentChange={handleContentChange}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={handleSaveContent}
                                                                onSaveToVault={handleSaveToVault}
                                                                onStartOver={handleStartOver}
                                                                isSavingToVault={isSavingToVault}
                                                            />
                                                        </div>
                                                    ) : (
                                                        /* Instagram Post / Coming Soon - Larger dimensions for full caption visibility */
                                                        <div className="relative aspect-[375/620] h-full max-h-full w-auto max-w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
                                                            <PlatformPreview
                                                                platform={activePlatform}
                                                                instagramContentType={instagramContentType}
                                                                contentType={contentType}
                                                                generatedContent={generatedContent}
                                                                guestName={episode?.guestName || episode?.founderName}
                                                                onContentChange={handleContentChange}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={handleSaveContent}
                                                                episode={episode}
                                                                onSaveToVault={handleSaveToVault}
                                                                onStartOver={handleStartOver}
                                                                isSavingToVault={isSavingToVault}
                                                                onOpenVault={() => setIsVaultOpen(true)}
                                                                onComingSoonTemplateSelect={handleComingSoonTemplateSelect}
                                                                selectedComingSoonTemplate={selectedComingSoonTemplate}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                /* Phone Frame Container for other platforms */
                                                <div className="flex-1 flex items-center justify-center">
                                                    <div className={`relative w-[280px] h-[560px] ${PLATFORMS[activePlatform].bgColor} rounded-[3rem] p-2 shadow-2xl border-2 ${PLATFORMS[activePlatform].borderColor}`}>
                                                        {/* Phone Notch */}
                                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl" />

                                                        {/* Screen Content */}
                                                        <div className="w-full h-full rounded-[2.5rem] overflow-hidden bg-white">
                                                            <PlatformPreview
                                                                platform={activePlatform}
                                                                contentType={contentType}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Pane - AI Assistant */}
                                    <div className="w-1/2 flex flex-col">
                                        <AIAssistantPanel
                                            platform={activePlatform}
                                            contentType={contentType}
                                            episode={episode}
                                            planContent={planContent}
                                            selectedComingSoonTemplate={selectedComingSoonTemplate}
                                            onGeneratedContent={(content) => {
                                                setGeneratedContent(content);
                                                if (onGenerate) {
                                                    onGenerate(content);
                                                }
                                            }}
                                            onOpenVault={() => setIsVaultOpen(true)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Vault Drawer - Rendered at app level, outside modal */}
            <VaultDrawer
                isOpen={isVaultOpen}
                onClose={() => setIsVaultOpen(false)}
                episode={episode}
            />
        </AnimatePresence>
    );

    // Use portal to render outside the container hierarchy
    return createPortal(modalContent, document.body);
};

export default ContentCreationModal;